import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Layout, Menu } from 'antd';
import {
  AimOutlined,
  UnorderedListOutlined,
  SyncOutlined,
  UserOutlined,
  LikeOutlined,
  SettingOutlined,
  MailOutlined,
  TeamOutlined,
  QuestionCircleOutlined,
  ApartmentOutlined,
  CommentOutlined,
  PieChartOutlined,
  CodeOutlined,
  BuildOutlined,
  TrophyOutlined,
  FileMarkdownOutlined,
  TagOutlined,
  WechatOutlined,
  FolderOpenOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';

import Logo from '../../../assets/img/logo.png';
import Img1 from '../../../assets/img/img.png';
import { useHistory } from 'react-router-dom';
import './WebUxLayout.less';
import { useConnection, useUserInfo } from '../../../connection/Application';
import { useQueryParam } from 'use-query-params';

const { Sider } = Layout;

// eslint-disable-next-line @typescript-eslint/ban-types
type Props = {};

export const WebUxSider: FC<Props> = () => {
  const history = useHistory();
  const [cycle] = useQueryParam<number | undefined>('cycle');
  const user = useUserInfo();
  const [isApprover, setIsApprover] = useState<boolean>(false);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const connection = useConnection();
  const navigate: any = useCallback(
    (info: { key: string }) => {
      if (info.key !== history.location.pathname)
        history.push(info.key + (cycle ? `?cycle=${cycle}` : ''));
    },
    [cycle, history],
  );
  const getSelectedPath = useMemo(() => {
    const h = history.location.pathname.split('/');
    return '/webux/' + h[2];
  }, [history.location.pathname]);
  const canViewReports =
    user?.roles?.includes('hr') ||
    user?.roles?.includes('admin') ||
    user?.roles?.includes('manger') ||
    user?.isLeader;
  const canViewFeedbackReports = canViewReports;

  useEffect(() => {
    let mounted = true;
    // Attempt to avoid state updates after unmount by guarding with a mounted flag
    connection
      .get(`users/internal-customer/approve`)
      .then((res) => {
        if (!mounted) return;
        setIsApprover(res?.approve);
      })
      .catch(() => {
        // ignore errors for sidebar capability detection
      });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line
  }, []);

  return (
    <Sider
      className="webux-sider"
      theme={'dark'}
      breakpoint="lg"
      collapsible
      width="250"
      collapsedWidth="80"
      onBreakpoint={() => {
        // console.log(broken);
      }}
      onCollapse={(collapsed) => {
        setCollapsed(collapsed);
        // console.log(collapsed, type);
      }}>
      <img alt="logo" style={{ margin: 'auto' }} src={collapsed ? Img1 : Logo} />
      <Menu
        theme={'dark'}
        mode="inline"
        defaultSelectedKeys={['4']}
        selectedKeys={[`${getSelectedPath}`]}>
        <Menu.Item key={`/webux/my-goals`} icon={<AimOutlined />} onClick={navigate}>
          Objectives
        </Menu.Item>
        <Menu.Item key="/webux/dashboard" icon={<TrophyOutlined />} onClick={navigate}>
          Performance Results
        </Menu.Item>
        {(user?.roles?.includes('hr') || user?.roles?.includes('admin')) && (
          <Menu.Item key="/webux/employees" icon={<UserOutlined />} onClick={navigate}>
            Employees
          </Menu.Item>
        )}
        {canViewReports && (
          <Menu.SubMenu
            key="/webux/performance-result"
            title="Reports"
            icon={<FolderOpenOutlined />}>
            <Menu.Item
              key="/webux/performance-result/employees"
              icon={<PieChartOutlined />}
              onClick={navigate}>
              Performance
            </Menu.Item>
            <Menu.Item
              key="/webux/performance-result/goals"
              icon={<AimOutlined />}
              onClick={navigate}>
              Objectives
            </Menu.Item>
            {canViewFeedbackReports && (
              <Menu.Item
                key="/webux/performance-result/user-feedback"
                icon={<WechatOutlined />}
                onClick={navigate}>
                Feedbacks
              </Menu.Item>
            )}
          </Menu.SubMenu>
        )}
        {
          <Menu.Item key="/webux/teams" icon={<ApartmentOutlined />} onClick={navigate}>
            Teams
          </Menu.Item>
        }
        <Menu.Item key="/webux/peer-reviews" icon={<LikeOutlined />} onClick={navigate}>
          Peer / Subordinate Reviews
        </Menu.Item>
        {user?.isLeader && (
          <Menu.Item
            key="/webux/peer-reviews-approve"
            icon={<CommentOutlined />}
            onClick={navigate}>
            Peer Review Approvals
          </Menu.Item>
        )}
        <Menu.Item key="/webux/internal-customers" icon={<TeamOutlined />} onClick={navigate}>
          Internal Customers
        </Menu.Item>
        {isApprover && (
          <Menu.Item
            key="/webux/internal-customer-answers"
            icon={<ApartmentOutlined />}
            onClick={navigate}>
            Internal Customer Responses
          </Menu.Item>
        )}
        {(user?.roles?.includes('hr') || user?.roles?.includes('admin')) && (
          <Menu.Item key="/webux/performance-cycle" icon={<SyncOutlined />} onClick={navigate}>
            Appraisal Cycles
          </Menu.Item>
        )}
        {(user?.roles?.includes('hr') || user?.roles?.includes('admin')) && (
          <Menu.SubMenu
            key="/webux/criterias"
            title="Criteria Settings"
            icon={<UnorderedListOutlined />}>
            <Menu.Item key="/webux/criteria" icon={<QuestionCircleOutlined />} onClick={navigate}>
              Criterias
            </Menu.Item>
            <Menu.Item key="/webux/criteria-set" icon={<AppstoreOutlined />} onClick={navigate}>
              Criteria Sets
            </Menu.Item>
          </Menu.SubMenu>
        )}
        {(user?.roles?.includes('hr') || user?.roles?.includes('admin') || user?.isLeader) && (
          <Menu.Item key={`/webux/setup`} icon={<SettingOutlined />} onClick={navigate}>
            360° Configuration
          </Menu.Item>
        )}
        {(user?.roles?.includes('admin') || user?.roles?.includes('hr')) && (
          <Menu.SubMenu key="/webux/masterdata" title="MasterData" icon={<FileMarkdownOutlined />}>
            <Menu.Item key="/webux/enum" onClick={navigate} icon={<BuildOutlined />}>
              Enums
            </Menu.Item>
            <Menu.Item key="/webux/user-groups" icon={<TeamOutlined />} onClick={navigate}>
              User Groups
            </Menu.Item>
            <Menu.Item key="/webux/designation" onClick={navigate} icon={<TagOutlined />}>
              Designations
            </Menu.Item>
            <Menu.Item key="/webux/configurations" onClick={navigate} icon={<CodeOutlined />}>
              System Variables
            </Menu.Item>
            <Menu.Item key="/webux/email-template" onClick={navigate} icon={<MailOutlined />}>
              Email Templates
            </Menu.Item>
          </Menu.SubMenu>
        )}
      </Menu>
    </Sider>
  );
};
